import { GeneratedImage } from '../hooks/useImageGeneration';

const STORAGE_KEY = 'user_stickers';
const MAX_STICKERS = 500;
// const API_BASE_URL = process.env.REACT_APP_API_URL || ''; // Sequestered for potential future use

class ServerStorageService {
  /**
   * Get all images from localStorage (single-player mode)
   */
  async getAllImages(): Promise<GeneratedImage[]> {
    const localImages = this.getLocalImages();
    console.log(`Loaded ${localImages.length} stickers from localStorage`);
    return localImages;
  }

  /**
   * Save an image (localStorage only for single-player mode)
   */
  async saveImage(image: GeneratedImage): Promise<boolean> {
    this.saveLocalImage(image);
    return true;
  }

  // SEQUESTERED: Server storage implementation for potential future use
  /*
  async saveImageToServer(image: GeneratedImage): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/images/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: image.id,
          url: image.url,
          prompt: image.prompt,
          timestamp: image.timestamp.toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save image');
      }

      const data = await response.json();

      // Also save to localStorage as backup
      if (data.success) {
        this.saveLocalImage(image);
      }

      return data.success;
    } catch (error) {
      console.error('Error saving image to server:', error);
      // Fallback to localStorage
      this.saveLocalImage(image);
      return true;
    }
  }
  */

  /**
   * Save multiple images at once
   */
  async saveImages(images: GeneratedImage[]): Promise<void> {
    for (const image of images) {
      await this.saveImage(image);
    }
  }

  /**
   * Delete an image (localStorage only for single-player mode)
   */
  async deleteImage(imageId: string): Promise<boolean> {
    this.deleteLocalImage(imageId);
    return true;
  }

  // SEQUESTERED: Server delete implementation for potential future use
  /*
  async deleteImageFromServer(imageId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/images/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ imageId })
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      const data = await response.json();

      // Also delete from localStorage
      if (data.success) {
        this.deleteLocalImage(imageId);
      }

      return data.success;
    } catch (error) {
      console.error('Error deleting image from server:', error);
      // Fallback to localStorage
      this.deleteLocalImage(imageId);
      return true;
    }
  }
  */

  /**
   * Clear all images (localStorage only for single-player mode)
   */
  async clearAllImages(): Promise<boolean> {
    this.clearLocalImages();
    return true;
  }

  // SEQUESTERED: Server clear implementation for potential future use
  /*
  async clearAllImagesFromServer(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/images/clear`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to clear images');
      }

      const data = await response.json();

      // Also clear localStorage
      if (data.success) {
        this.clearLocalImages();
      }

      return data.success;
    } catch (error) {
      console.error('Error clearing images from server:', error);
      // Fallback to localStorage
      this.clearLocalImages();
      return true;
    }
  }
  */

  // Local storage fallback methods
  private getLocalImages(): GeneratedImage[] {
    try {
      const stored = localStorage.getItem('adnan_generated_images');
      if (!stored) return [];

      const images = JSON.parse(stored) as GeneratedImage[];
      return images.map(img => ({
        ...img,
        timestamp: new Date(img.timestamp)
      }));
    } catch (error) {
      return [];
    }
  }

  private saveLocalImage(image: GeneratedImage): void {
    try {
      const stored = this.getLocalImages();
      stored.unshift(image);

      // Increased limit from 100 to 500 images
      if (stored.length > 500) {
        stored.splice(500);
      }

      localStorage.setItem('adnan_generated_images', JSON.stringify(stored));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      // If localStorage is full (QuotaExceededError), try with fewer images
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, reducing to 250 images');
        try {
          const stored = this.getLocalImages();
          stored.unshift(image);
          // Keep only 250 most recent images
          if (stored.length > 250) {
            stored.splice(250);
          }
          localStorage.setItem('adnan_generated_images', JSON.stringify(stored));
        } catch (retryError) {
          console.error('Still failed after reducing images:', retryError);
        }
      }
    }
  }

  private deleteLocalImage(imageId: string): void {
    try {
      const stored = this.getLocalImages();
      const filtered = stored.filter(img => img.id !== imageId);
      localStorage.setItem('adnan_generated_images', JSON.stringify(filtered));
    } catch (error) {
      console.error('Error deleting from localStorage:', error);
    }
  }

  private clearLocalImages(): void {
    try {
      localStorage.removeItem('adnan_generated_images');
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }
}

export default new ServerStorageService();